import type {
  ChangePropagation,
  ConsumerContractRegistryManifest,
  DomainOwner,
  FieldIndexEntry,
  FlatSubscriptionRow,
  NewValuePropagationKind,
  OperationEffect,
  RegistryFilterState,
  RiskClassification,
  SubscriptionKind,
  ValueHandlingKind,
  ValuesSourceKind,
} from './consumerContractRegistry.types';
import { flattenWithValueHandling } from './consumerContractRegistry.valueHandling';
import { resolveSeedKeyForField } from './consumerContractRegistry.seedDefinitions';
import { PUBLIC_PDF_MAPPER, SHARED_SUMMARY_BUILDER } from './consumerContractRegistry.relationships';

export function flattenManifestSubscriptions(
  manifest: ConsumerContractRegistryManifest,
): FlatSubscriptionRow[] {
  return flattenWithValueHandling(manifest);
}

const RISK_RANK: Record<RiskClassification, number> = {
  expected_neutral: 0,
  attention: 1,
  drift_risk: 2,
  conflicting_authority: 3,
};

const PROPAGATION_ATTENTION: ChangePropagation[] = ['separate_update_required', 'unknown'];

function mergeRisk(a: RiskClassification, b: RiskClassification): RiskClassification {
  return RISK_RANK[a] >= RISK_RANK[b] ? a : b;
}

function mergePropagation(a: ChangePropagation, b: ChangePropagation): ChangePropagation {
  if (PROPAGATION_ATTENTION.includes(a)) return a;
  if (PROPAGATION_ATTENTION.includes(b)) return b;
  return a;
}

export function dedupeSubscribersForFieldIndex(subscribers: FlatSubscriptionRow[]): FlatSubscriptionRow[] {
  const byArtifactField = new Map<string, FlatSubscriptionRow>();

  for (const row of subscribers) {
    if (row.excludeFromFieldIndex) continue;

    const key = `${row.artifactId}::${row.fieldKey}`;
    const existing = byArtifactField.get(key);
    if (!existing) {
      byArtifactField.set(key, { ...row, subscriptionKinds: [row.subscriptionKind] });
      continue;
    }

    const kinds = Array.from(
      new Set<SubscriptionKind>([...(existing.subscriptionKinds || [existing.subscriptionKind]), row.subscriptionKind]),
    );
    byArtifactField.set(key, {
      ...existing,
      subscriptionKind: kinds.includes('direct') ? 'direct' : kinds.includes('indirect') ? 'indirect' : existing.subscriptionKind,
      subscriptionKinds: kinds,
      riskClassification: mergeRisk(existing.riskClassification, row.riskClassification),
      changePropagation: mergePropagation(existing.changePropagation, row.changePropagation),
      transformationRule:
        kinds.length > 1
          ? `${existing.transformationRule}; Also: ${row.transformationRule}`
          : existing.transformationRule,
      codeReference:
        existing.codeReference !== row.codeReference
          ? `${existing.codeReference}; ${row.codeReference}`
          : existing.codeReference,
    });
  }

  return Array.from(byArtifactField.values());
}

export function buildFieldIndex(manifest: ConsumerContractRegistryManifest): FieldIndexEntry[] {
  const flat = flattenManifestSubscriptions(manifest);
  const byKey = new Map<string, FieldIndexEntry>();

  for (const row of flat) {
    if (row.excludeFromFieldIndex) continue;

    const existing = byKey.get(row.fieldKey);
    if (existing) {
      existing.subscribers.push(row);
    } else {
      byKey.set(row.fieldKey, {
        fieldKey: row.fieldKey,
        subscribedField: row.subscribedField,
        sourcePath: row.sourcePath,
        seedKey: row.seedKey ?? resolveSeedKeyForField(row.fieldKey),
        subscribers: [row],
      });
    }
  }

  return Array.from(byKey.values())
    .map((entry) => ({
      ...entry,
      subscribers: dedupeSubscribersForFieldIndex(entry.subscribers),
    }))
    .sort((a, b) => a.subscribedField.localeCompare(b.subscribedField));
}

export function countFieldIndexSubscribers(entry: FieldIndexEntry): number {
  return entry.subscribers.length;
}

export function getFieldIndexSubscribers(
  manifest: ConsumerContractRegistryManifest,
  fieldKey: string,
): FlatSubscriptionRow[] {
  const entry = buildFieldIndex(manifest).find((e) => e.fieldKey === fieldKey);
  return entry?.subscribers ?? [];
}

export function countSubscriptionsByRisk(
  manifest: ConsumerContractRegistryManifest,
): Record<RiskClassification, number> {
  const counts: Record<RiskClassification, number> = {
    expected_neutral: 0,
    attention: 0,
    drift_risk: 0,
    conflicting_authority: 0,
  };
  for (const row of flattenManifestSubscriptions(manifest)) {
    counts[row.riskClassification] += 1;
  }
  return counts;
}

export function countSubscriptionsByPropagation(
  manifest: ConsumerContractRegistryManifest,
): Record<ChangePropagation, number> {
  const counts: Record<ChangePropagation, number> = {
    automatic_shared: 0,
    separate_update_required: 0,
    generated_from_shared_contract: 0,
    no_propagation_required: 0,
    unknown: 0,
  };
  for (const row of flattenManifestSubscriptions(manifest)) {
    counts[row.changePropagation] += 1;
  }
  return counts;
}

function matchesSearch(row: FlatSubscriptionRow, search: string): boolean {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    row.subscribedField,
    row.fieldKey,
    row.sourcePath,
    row.currentReadSource,
    row.outputLabelOrSlot,
    row.transformationRule,
    row.codeReference,
    row.artifactName,
    row.artifactId,
    row.viaBuilder || '',
    row.subscriptionKind,
    row.domainOwner,
    row.accessMode,
    row.operation,
    row.writeBack,
    row.additionalPersistedCopy,
    row.riskClassification,
    row.riskRationale,
    row.changePropagation,
    row.propagationRationale,
    row.safeguard,
    row.relationshipKind,
    buildEffectStatement(row),
    labelForRoute(row.subscriptionKind, row.viaBuilder),
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

export function filterFlatSubscriptions(
  rows: FlatSubscriptionRow[],
  filters: RegistryFilterState,
): FlatSubscriptionRow[] {
  return rows.filter((row) => {
    if (filters.artifactId && row.artifactId !== filters.artifactId) return false;
    if (filters.audience && row.audience !== filters.audience) return false;
    if (filters.sourceCategory && row.sourceCategory !== filters.sourceCategory) return false;
    if (filters.usage && row.usage !== filters.usage) return false;
    if (filters.operation && row.operation !== filters.operation) return false;
    if (filters.riskClassification && row.riskClassification !== filters.riskClassification) return false;
    if (filters.changePropagation && row.changePropagation !== filters.changePropagation) return false;
    if (filters.valueHandling && row.valueHandling !== filters.valueHandling) return false;
    if (filters.valuesSource && row.valuesSource !== filters.valuesSource) return false;
    if (filters.newValuePropagation && row.newValuePropagation !== filters.newValuePropagation) return false;
    if (!matchesSearch(row, filters.search)) return false;
    return true;
  });
}

export function filterFieldIndex(
  entries: FieldIndexEntry[],
  filters: RegistryFilterState,
): FieldIndexEntry[] {
  return entries
    .map((entry) => ({
      ...entry,
      subscribers: filterFlatSubscriptions(entry.subscribers, filters),
    }))
    .filter((entry) => entry.subscribers.length > 0);
}

export function countSubscriptions(manifest: ConsumerContractRegistryManifest): number {
  return manifest.artifacts.reduce((sum, a) => sum + a.subscriptions.length, 0);
}

export function labelForDomainOwner(value: DomainOwner): string {
  switch (value) {
    case 'canonical_window':
      return 'Canonical window';
    case 'quote':
      return 'Quote';
    case 'derived_cache':
      return 'Derived / cache';
    default:
      return value;
  }
}

export function labelForSourceCategory(value: string): string {
  switch (value) {
    case 'canonical_payload':
      return 'Canonical payload';
    case 'quote_owned':
      return 'Quote-owned';
    case 'derived_cache':
      return 'Derived / cache';
    default:
      return value;
  }
}

export function labelForUsage(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function labelForRenderSource(value: string): string {
  switch (value) {
    case 'live_render':
      return 'Live render';
    case 'stored_png':
      return 'Stored PNG';
    case 'mixed':
      return 'Mixed';
    default:
      return value;
  }
}

export function labelForAudience(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function labelForRoute(kind: SubscriptionKind, viaBuilder?: string): string {
  if (viaBuilder === PUBLIC_PDF_MAPPER) return PUBLIC_PDF_MAPPER;
  if (viaBuilder === SHARED_SUMMARY_BUILDER) return 'Via shared builder';
  switch (kind) {
    case 'direct':
      return 'Referenced';
    case 'indirect':
      return viaBuilder || 'Via shared builder';
    case 'aggregate':
      return viaBuilder === PUBLIC_PDF_MAPPER ? PUBLIC_PDF_MAPPER : 'Shared builder';
    default:
      return kind;
  }
}

export function routeTooltip(kind: SubscriptionKind, viaBuilder?: string): string {
  if (viaBuilder === PUBLIC_PDF_MAPPER) {
    return 'Route via Public PDF mapper in the Supabase Edge Function. Parallels the shared application mapper against sanitised meta_json. Route describes access only — not operation risk.';
  }
  if (viaBuilder === SHARED_SUMMARY_BUILDER) {
    return 'Route via getDesignerSummaryRows shared builder. Route describes access only — not operation risk.';
  }
  switch (kind) {
    case 'direct':
      return 'Direct read from declared source path.';
    case 'indirect':
      return 'Field reached through a shared builder or mapper.';
    case 'aggregate':
      return 'Shared builder output — see decomposed field rows.';
    default:
      return '';
  }
}

export function labelForAccessMode(mode: string): string {
  switch (mode) {
    case 'direct_read':
      return 'Direct read';
    case 'indirect_read_shared_builder':
      return 'Indirect read (shared builder)';
    case 'derived_calculated':
      return 'Derived / calculated';
    case 'cached_derivative':
      return 'Cached derivative';
    default:
      return mode;
  }
}

export function labelForOperation(operation: OperationEffect): string {
  switch (operation) {
    case 'read_only':
      return 'Read only';
    case 'read_and_format':
      return 'Read and format';
    case 'calculate_derive':
      return 'Calculated';
    case 'create_cached_representation':
      return 'Creates cached representation';
    case 'persist_duplicate':
      return 'Additional stored copy';
    case 'mutate_source':
      return 'Writes back';
    default:
      return operation;
  }
}

export function labelForWriteBack(writeBack: string): string {
  switch (writeBack) {
    case 'none':
      return 'None';
    case 'writes_derivative_cache_only':
      return 'Writes derivative/cache only';
    case 'writes_source':
      return 'Writes source';
    default:
      return writeBack;
  }
}

export function labelForAdditionalCopy(copy: string): string {
  switch (copy) {
    case 'none':
      return 'None';
    case 'derived_cache':
      return 'Uses derived cache';
    case 'independently_editable_copy':
      return 'Independently editable copy';
    default:
      return copy;
  }
}

export function labelForRiskClassification(risk: RiskClassification): string {
  switch (risk) {
    case 'expected_neutral':
      return 'Expected';
    case 'attention':
      return 'Attention';
    case 'drift_risk':
      return 'Drift risk';
    case 'conflicting_authority':
      return 'Conflicting authority';
    default:
      return risk;
  }
}

export function labelForChangePropagation(propagation: ChangePropagation): string {
  switch (propagation) {
    case 'automatic_shared':
      return 'Automatic / shared';
    case 'separate_update_required':
      return 'Separate update required';
    case 'generated_from_shared_contract':
      return 'Generated from shared contract';
    case 'no_propagation_required':
      return 'No propagation required';
    case 'unknown':
      return 'Unknown';
    default:
      return propagation;
  }
}

export function labelForSafeguard(safeguard: string): string {
  switch (safeguard) {
    case 'contract_parity_test':
      return 'Contract / parity test';
    case 'none':
      return 'None';
    default:
      return safeguard;
  }
}

export function labelForRelationshipKind(kind: string): string {
  switch (kind) {
    case 'canonical_reference':
      return 'Canonical reference';
    case 'compatibility_overlay':
      return 'Compatibility overlay';
    case 'standard':
      return 'Standard';
    default:
      return kind;
  }
}

export function buildEffectStatement(
  row: Pick<
    FlatSubscriptionRow,
    | 'artifactName'
    | 'subscribedField'
    | 'domainOwner'
    | 'subscriptionKind'
    | 'viaBuilder'
    | 'accessMode'
    | 'operation'
    | 'writeBack'
    | 'additionalPersistedCopy'
    | 'changePropagation'
  >,
): string {
  const owner = labelForDomainOwner(row.domainOwner).toLowerCase();
  const route =
    row.subscriptionKind === 'indirect' && row.viaBuilder
      ? ` through ${labelForRoute(row.subscriptionKind, row.viaBuilder)}`
      : row.accessMode === 'cached_derivative'
        ? ' from stored preview path'
        : '';
  const op = labelForOperation(row.operation).toLowerCase();
  const wb = row.writeBack === 'none' ? 'no write-back' : labelForWriteBack(row.writeBack).toLowerCase();
  const copy =
    row.additionalPersistedCopy === 'none'
      ? 'no additional persisted copy'
      : labelForAdditionalCopy(row.additionalPersistedCopy).toLowerCase();
  const propagation = labelForChangePropagation(row.changePropagation).toLowerCase();
  return `${row.artifactName} reads the ${owner} ${row.subscribedField}${route}, ${op}, ${wb}, ${copy}. Change propagation: ${propagation}.`;
}

/** @deprecated Use labelForRoute */
export function labelForRouteKind(kind: SubscriptionKind): string {
  return labelForRoute(kind);
}

/** @deprecated Use labelForRoute */
export function labelForSubscriptionKind(kind: SubscriptionKind, viaBuilder?: string): string {
  return labelForRoute(kind, viaBuilder);
}

export function subscriptionKindTooltip(kind: SubscriptionKind, viaBuilder?: string): string {
  return routeTooltip(kind, viaBuilder);
}

export function routeKindTooltip(kind: SubscriptionKind, viaBuilder?: string): string {
  return routeTooltip(kind, viaBuilder);
}

export const EMPTY_REGISTRY_FILTERS: RegistryFilterState = {
  search: '',
  artifactId: '',
  audience: '',
  sourceCategory: '',
  usage: '',
  operation: '',
  riskClassification: '',
  changePropagation: '',
  valueHandling: '',
  valuesSource: '',
  newValuePropagation: '',
};

export function labelForValueHandling(value: ValueHandlingKind): string {
  switch (value) {
    case 'value_agnostic':
      return 'Value-agnostic';
    case 'value_aware':
      return 'Value-aware';
    case 'pass_through_with_constraints':
      return 'Pass-through with constraints';
    case 'unknown':
      return 'Unknown';
    default:
      return value;
  }
}

export function labelForValuesSource(value: ValuesSourceKind): string {
  switch (value) {
    case 'canonical_contract':
      return 'Canonical contract';
    case 'shared_configuration':
      return 'Shared configuration';
    case 'local_hard_coded_list':
      return 'Local hard-coded list';
    case 'sanitisation_whitelist':
      return 'Sanitisation whitelist';
    case 'supplier_external_mapping':
      return 'Supplier/external mapping';
    case 'unrestricted':
      return 'Unrestricted';
    case 'unknown':
      return 'Unknown';
    default:
      return value;
  }
}

export function labelForNewValuePropagation(value: NewValuePropagationKind): string {
  switch (value) {
    case 'automatic':
      return 'Automatic';
    case 'verification_only':
      return 'Verification only';
    case 'mapping_update_required':
      return 'Mapping update required';
    case 'validation_sanitisation_update_required':
      return 'Validation/sanitisation update required';
    case 'supplier_contract_update_required':
      return 'Supplier contract update required';
    case 'unknown':
      return 'Unknown';
    default:
      return value;
  }
}

export { PUBLIC_PDF_MAPPER, SHARED_SUMMARY_BUILDER };
